import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getUserPlaylists } from "@/lib/spotify/api";
import { useSpotifyAuth } from "@/hooks/useSpotifyAuth";
import { useSettings } from "@/hooks/useSettings";
import type { SpotifyPlaylist } from "@/lib/spotify/types";
import { cn } from "@/lib/utils";

const SpotifyPlaylistPicker: React.FC = () => {
  const { getValidToken } = useSpotifyAuth();
  const { settings, setSound } = useSettings();

  const { data, isLoading, error } = useQuery({
    queryKey: ["spotify-playlists"],
    queryFn: async () => {
      const token = await getValidToken();
      return getUserPlaylists(token);
    },
    staleTime: 5 * 60 * 1000,
  });

  const handleSelect = (playlist: SpotifyPlaylist) => {
    setSound({
      spotify: {
        selectedPlaylistId: playlist.id,
        selectedPlaylistUri: playlist.uri,
        selectedPlaylistName: playlist.name,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2">
        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
        <span className="text-sm text-muted-foreground">Loading playlists...</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        Failed to load playlists: {error instanceof Error ? error.message : "Unknown error"}
      </p>
    );
  }

  if (!data?.items.length) {
    return <p className="text-sm text-muted-foreground">No playlists found</p>;
  }

  return (
    <div className="max-h-48 overflow-y-auto space-y-1 rounded-md border border-border p-1">
      {data.items.map((playlist) => {
        const isSelected = settings.sound.spotify.selectedPlaylistId === playlist.id;
        const image = playlist.images?.[0]?.url;

        return (
          <button
            key={playlist.id}
            onClick={() => handleSelect(playlist)}
            className={cn(
              "w-full flex items-center gap-3 p-2 rounded-md text-left transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
              isSelected && "bg-accent text-accent-foreground",
            )}
          >
            {image ? (
              <img
                src={image}
                alt={playlist.name}
                className="h-10 w-10 rounded object-cover flex-shrink-0"
              />
            ) : (
              <div className="h-10 w-10 rounded bg-muted flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{playlist.name}</p>
              <p className="text-xs text-muted-foreground">
                {playlist.tracks.total} tracks
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default SpotifyPlaylistPicker;
